import Entity from "_framework/entities/entity";
import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import NetworkDomain from "_shared/network-domain/network-domain";
import LegalEntitySchema, { LegalEntityJSON, LegalEntityParams } from "legal-entities/_schema/legal-entity-schema";
import EntityNameSchema from "legal-entities/entity-names/_schema/entity-name-schema";
import EntityName from "legal-entities/entity-names/entity-name";
import UserEntityInfo from "users/information/entity-information/user-entity-info";
import UserEntityInfoSchema from "users/information/entity-information/user-entity-info-schema";
import { User } from "users/user";

export default class LegalEntity extends Entity<LegalEntity, LegalEntityJSON, LegalEntityParams> {
  public static readonly EXAMPLE: LegalEntity = LegalEntity.create({
    id: Guid.generate(),
    name: EntityName.create("Test Entity"),
    managerIds: [],
    isVendor: false,
    isClient: false,
    emailDomains: [],
  });

  public static create(values: LegalEntityParams): LegalEntity {
    const parsed = LegalEntitySchema.validate(values);
    return new LegalEntity(parsed);
  }

  public static fromJSON(json: LegalEntityJSON): LegalEntity {
    if (isNullOrUndefined(json)) return json;
    const params = LegalEntitySchema.deserialize(json);
    return new LegalEntity(params);
  }

  public static fromJSONArray(jsonArray: unknown): LegalEntity[] | null | undefined {
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    return LegalEntitySchema.deserializeJSONArray(jsonArray).map((json) => new LegalEntity(json));
  }

  public readonly id: Guid;
  public readonly name?: EntityName;
  public readonly managerIds: Guid[] = [];
  public readonly isVendor?: boolean;
  public readonly isClient?: boolean;
  public readonly emailDomains: NetworkDomain[] = [];

  public constructor(params: LegalEntityParams) {
    const validatedParams = LegalEntitySchema.validate(params);
    super(params);
    this.id = validatedParams.id;
    this.name = validatedParams.name;
    this.managerIds = validatedParams.managerIds ?? [];
    this.isClient = validatedParams.isClient;
    this.isVendor = validatedParams.isVendor;
    if (validatedParams.emailDomains) {
      this.emailDomains = validatedParams.emailDomains;
    }
  }

  public get info(): UserEntityInfo {
    const params = UserEntityInfoSchema.validate({
      ...this,
      entityId: this.id,
      entityName: new EntityName(EntityNameSchema.validate(this.name?.value)),
      isAdmin: false,
      isManager: false,
      isOfficer: false,
    });
    return new UserEntityInfo(params);
  }

  public toJSON(): LegalEntityJSON {
    return LegalEntitySchema.serialize(this);
  }

  public valueOf(): LegalEntityJSON {
    return this.toJSON();
  }

  public hasManager(user: User | null): boolean {
    if (!user?.id) return false;
    return this.managerIds.some((id) => id.isEqualTo(user.id));
  }

  public hasDomain(domain: NetworkDomain): boolean {
    return this.emailDomains.some((d) => d.isEqualTo(domain));
  }

  public isEqualTo(other: LegalEntity): boolean {
    return this.id.isEqualTo(other?.id);
  }

  public toString(): string {
    return `LegalEntity (
      id: ${this.id}
      name: ${this.name}
      isVendor: ${this.isVendor}
      isClient: ${this.isClient}
      emailDomains: [${this.emailDomains.map((d) => d.value).join(", ")}]
    )`;
  }
}

export class LegalEntityNotFoundError extends Error {
  constructor(id: Guid) {
    super(`Legal entity with id ${id} not found.`);
  }
}
